import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  connectAuthPost, connectUploadImage, getConnectToken, clearConnectSession
} from './connectApi';
import ConnectShell from './ConnectShell';
import ComposerImage from './ComposerImage';
import PostComments from './PostComments';
import ProfileLink from './ProfileLink';
import './styles/ConnectGroups.css';

const timeAgo = (seconds) => {
  const ms = seconds * 1000;
  const elapsed = Math.floor((Date.now() - ms) / 1000);
  if (elapsed < 60) return 'Just now';
  const minutes = Math.floor(elapsed / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

/**
 * One group: its details, a join button and, for members, the posts.
 * A post can be text, a photo, or both.
 */
export default function ConnectGroupDetail() {
  const { id } = useParams();
  const groupId = parseInt(id, 10);
  const navigate = useNavigate();

  const [group, setGroup] = useState(null);
  const [posts, setPosts] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const [openCommentsPostId, setOpenCommentsPostId] = useState(null);

  const [content, setContent] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [isPosting, setIsPosting] = useState(false);
  const imageInputRef = useRef(null);

  const loadPosts = useCallback(async (beforeId = 0) => {
    const payload = { group_id: groupId };
    if (beforeId) payload.before_id = beforeId;
    const result = await connectAuthPost('connect/groups/posts', payload);
    if (!result.success) {
      setError(result.response || 'Could not load the posts.');
      return;
    }
    const incoming = result.data.posts || [];
    setPosts(prev => (beforeId ? [...prev, ...incoming] : incoming));
    setHasMore(!!result.data.has_more);
  }, [groupId]);

  useEffect(() => {
    if (!getConnectToken()) {
      navigate('/connect');
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const result = await connectAuthPost('connect/groups/get', { group_id: groupId });
        if (cancelled) return;
        if (!result.success) {
          setError(result.response || 'Group not found.');
          return;
        }
        setGroup(result.data.group);
        if (result.data.group.is_member) await loadPosts();
      } catch (err) {
        if (!cancelled) setError('Could not reach the server.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [groupId, navigate, loadPosts]);

  const handleLogout = async () => {
    try { await connectAuthPost('connect/auth/logout'); } catch (err) { /* clear locally regardless */ }
    clearConnectSession();
    navigate('/connect');
  };

  const handleMembership = async () => {
    if (isBusy || !group) return;
    const leaving = group.is_member;
    if (leaving && !window.confirm(`Leave ${group.name}?`)) return;
    setIsBusy(true);
    setError('');
    try {
      const result = await connectAuthPost(leaving ? 'connect/groups/leave' : 'connect/groups/join', { group_id: groupId });
      if (!result.success) {
        setError(result.response || 'Could not do that.');
        return;
      }
      if (result.data.group) setGroup(result.data.group);
      if (leaving) setPosts([]);
      else await loadPosts();
    } catch (err) {
      setError('Could not reach the server.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleCreatePost = async () => {
    if ((!content.trim() && !imageFile) || isPosting) return;
    setIsPosting(true);
    setError('');
    try {
      let image = '';
      if (imageFile) {
        const upload = await connectUploadImage(imageFile, 'post');
        if (!upload.success) {
          setError(upload.response || 'Could not upload the photo.');
          return;
        }
        image = upload.data.url;
      }
      const result = await connectAuthPost('connect/groups/post', {
        group_id: groupId,
        content: content.trim(),
        image,
      });
      if (!result.success) {
        setError(result.response || 'Could not post that.');
        return;
      }
      setPosts(prev => [result.data.post, ...prev]);
      setContent('');
      setImageFile(null);
    } catch (err) {
      setError('Could not reach the server.');
    } finally {
      setIsPosting(false);
    }
  };

  const handleToggleLike = async (postId) => {
    try {
      const result = await connectAuthPost('connect/posts/like', { post_id: postId });
      if (result.success) {
        setPosts(prev => prev.map(p => (p.id === postId
          ? { ...p, liked_by_viewer: result.data.liked, likes: result.data.likes }
          : p)));
      }
    } catch (err) {
      setError('Could not reach the server.');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      const result = await connectAuthPost('connect/posts/delete', { post_id: postId });
      if (result.success) setPosts(prev => prev.filter(p => p.id !== postId));
      else setError(result.response || 'Could not delete that post.');
    } catch (err) {
      setError('Could not reach the server.');
    }
  };

  const handleLoadMore = async () => {
    if (!posts.length || isBusy) return;
    setIsBusy(true);
    try {
      await loadPosts(posts[posts.length - 1].id);
    } catch (err) {
      setError('Could not reach the server.');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <ConnectShell active="groups" onLogout={handleLogout}>
      <Link to="/connect/groups" className="group-back">← All groups</Link>

      {isLoading && <div className="cpx-state"><h3>Loading group</h3></div>}
      {error && <p className="feed-error">{error}</p>}

      {group && (
        <>
          <section className="group-head">
            <div
              className="group-head-image"
              style={group.image ? { backgroundImage: `url(${group.image})` } : undefined}
            />
            <div className="group-head-body">
              <div>
                <h1>{group.name}</h1>
                <span className="group-card-meta">
                  {group.member_count} member{group.member_count === 1 ? '' : 's'}
                </span>
              </div>
              <button
                type="button"
                className={group.is_member ? 'group-join-btn is-member' : 'group-join-btn'}
                onClick={handleMembership}
                disabled={isBusy}
              >
                {group.is_member ? 'Leave group' : 'Join group'}
              </button>
            </div>
            {group.description && <p className="group-head-desc">{group.description}</p>}
          </section>

          {!group.is_member && (
            <div className="cpx-state">
              <h3>Join to see what members are sharing</h3>
              <p>Posts in this group are visible to its members only.</p>
            </div>
          )}

          {group.is_member && (
            <div className="connect-post-composer">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={`Share something with ${group.name}...`}
                rows={3}
                maxLength={2000}
              />
              <ComposerImage file={imageFile} onClear={() => setImageFile(null)} />
              <div className="connect-composer-row">
                <button
                  type="button"
                  className="connect-composer-photo-btn"
                  onClick={() => imageInputRef.current?.click()}
                >
                  {imageFile ? '🖼️ Change photo' : '🖼️ Photo'}
                </button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    setImageFile(e.target.files[0] || null);
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  className="connect-composer-post-btn"
                  onClick={handleCreatePost}
                  disabled={isPosting || (!content.trim() && !imageFile)}
                >
                  {isPosting ? 'Posting...' : 'Post'}
                </button>
              </div>
            </div>
          )}

          {group.is_member && !isLoading && posts.length === 0 && (
            <div className="cpx-state"><h3>No posts yet. Be the first!</h3></div>
          )}

          {posts.map((post) => (
            <article key={post.id} className="connect-post-card">
              <div className="connect-post-header">
                <div className="connect-post-avatar">
                  <ProfileLink userId={post.user_id} avatar>
                    {post.author_picture ? (
                      <img src={post.author_picture} alt={post.author_name} />
                    ) : (
                      <div className="connect-post-avatar-placeholder">
                        {(post.author_name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </ProfileLink>
                </div>
                <div className="connect-post-meta">
                  <span className="connect-post-author">
                    <ProfileLink userId={post.user_id}>{post.is_own ? 'You' : post.author_name}</ProfileLink>
                  </span>
                  <span className="connect-post-time">{timeAgo(post.timestamp)}</span>
                </div>
                {post.is_own && (
                  <button type="button" className="group-post-delete" onClick={() => handleDelete(post.id)}>
                    Delete
                  </button>
                )}
              </div>

              {post.content && <p className="connect-post-content">{post.content}</p>}
              {post.image && (
                <div className="connect-post-image">
                  <img src={post.image} alt="" />
                </div>
              )}

              <div className="connect-post-actions">
                <button
                  type="button"
                  className={`connect-post-action ${post.liked_by_viewer ? 'liked' : ''}`}
                  onClick={() => handleToggleLike(post.id)}
                  aria-pressed={post.liked_by_viewer}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill={post.liked_by_viewer ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                  <span>{post.likes}</span>
                </button>
                <button
                  type="button"
                  className={`connect-post-action ${openCommentsPostId === post.id ? 'liked' : ''}`}
                  onClick={() => setOpenCommentsPostId(openCommentsPostId === post.id ? null : post.id)}
                  aria-expanded={openCommentsPostId === post.id}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  <span>{post.comments}</span>
                </button>
              </div>

              {openCommentsPostId === post.id && (
                <PostComments
                  postId={post.id}
                  onCountChange={(pid, count) => setPosts(prev => prev.map(p => (
                    p.id === pid ? { ...p, comments: count } : p
                  )))}
                />
              )}
            </article>
          ))}

          {hasMore && (
            <button type="button" className="feed-more-btn" onClick={handleLoadMore} disabled={isBusy}>
              {isBusy ? 'Loading...' : 'Show older posts'}
            </button>
          )}
        </>
      )}
    </ConnectShell>
  );
}
